import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useUpdateUserDetails } from "../hooks/use-update-user-details";
import { UserProfile } from "@/pages/users/types/user";
import { toast } from "@/hooks/use-toast";
import { X } from "lucide-react";
import { COUNTRIES } from "@/utils/country-list";
import {
  COUNTRY_CODES,
  parsePhoneNumber,
  combinePhoneNumber,
} from "@/utils/country-codes";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";

const LANGUAGE_OPTIONS = [
  "English",
  "Hindi",
  "Bengali",
  "Kannada",
  "Marathi",
  "Assamese",
  "Gujarati",
  "Tamil",
  "Kutchi",
  "Urdu",
  "Punjabi",
  "Sindhi",
  "Telugu",
  "Malayalam",
  "Other",
];

const SEX_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

const DOCUMENT_TYPES = [
  { value: "Mental health", label: "Mental Health" },
  { value: "Men's health", label: "Men's Health" },
  { value: "Women's health", label: "Women's Health" },
  { value: "Couple's Therapy", label: "Couple's Therapy" },
  { value: "Family Therapy", label: "Family Therapy" },
] as const;

const phoneNumberRegex = /^\d{7,15}$/;

const updateUserDetailsSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email().optional().or(z.literal("")),
    phone_country_code: z.string().optional().or(z.literal("")),
    phone_number: z
      .string()
      .optional()
      .refine((val) => !val || val === "" || phoneNumberRegex.test(val), {
        message: "Phone number must be 7-15 digits",
      })
      .or(z.literal("")),
    country: z.string().optional().or(z.literal("")),
    region_id: z.string().optional().or(z.literal("")),
    age: z.number().int().positive().optional().or(z.null()),
    language: z.array(z.string()).optional().default([]),
    sex: z.string().optional().or(z.literal("")),
    hrc_status: z
      .enum(["NOT_INITIATED", "PENDING", "APPROVED", "REJECTED"])
      .optional(),
    mc_status: z
      .enum(["NOT_INITIATED", "PENDING", "APPROVED", "REJECTED"])
      .optional(),
    documents: z.array(z.object({
      type: z.string(),
      id: z.string().min(1, "Document ID is required"),
    })).optional().default([]),
    is_test_user: z.boolean().optional().default(false),
    allow_onboarding_flow: z.boolean().optional().default(false),
  })
  .refine(
    (data) => {
      if (data.phone_number && data.phone_number !== "") {
        return data.phone_country_code && data.phone_country_code !== "";
      }
      return true;
    },
    {
      message: "Country code is required when phone number is provided",
      path: ["phone_country_code"],
    }
  )
  .refine(
    (data) => {
      // Ensure max 1 document of each type
      const types = data.documents?.map(doc => doc.type) || [];
      return types.length === new Set(types).size;
    },
    {
      message: "Each document type can only be added once",
      path: ["documents"],
    }
  );

type UpdateUserDetailsFormData = z.infer<typeof updateUserDetailsSchema>;

interface UpdateUserDetailsDialogProps {
  user: UserProfile;
  onClose: () => void;
}

export function UpdateUserDetailsDialog({
  user,
  onClose,
}: UpdateUserDetailsDialogProps) {
  const { mutate: updateUserDetails, isPending } = useUpdateUserDetails();
  const { data: regions } = useActiveRegions();

  const existingPhone =
    user.preferred_phone_number || user.phone_numbers[0] || "";
  const { countryCode, number } = parsePhoneNumber(existingPhone);

  const form = useForm<UpdateUserDetailsFormData>({
    resolver: zodResolver(updateUserDetailsSchema),
    mode: "onChange",
    defaultValues: {
      name: user.full_name || "",
      email: user.preferred_email || user.emails[0] || "",
      phone_country_code: countryCode || "+91",
      phone_number: number,
      country: user.country || "",
      region_id: user.region_id || "",
      age: user.age || null,
      language: Array.isArray(user.language_preference)
        ? user.language_preference
        : user.language_preference
        ? [user.language_preference]
        : [],
      sex: user.sex || "",
      hrc_status: user.hrc_status ?? "NOT_INITIATED",
      mc_status: user.mc_status ?? "NOT_INITIATED",
      documents: user.document_ids
        ? Object.entries(user.document_ids).map(([type, id]) => ({ type, id: id as string }))
        : [],
      is_test_user: user.is_test_user ?? false,
      allow_onboarding_flow: user.allow_onboarding_flow ?? false,
    },
  });

  useEffect(() => {
    const existingPhone =
      user.preferred_phone_number || user.phone_numbers[0] || "";
    const { countryCode: parsedCode, number: parsedNumber } =
      parsePhoneNumber(existingPhone);

    form.reset({
      name: user.full_name || "",
      email: user.preferred_email || user.emails[0] || "",
      phone_country_code: parsedCode || "+91",
      phone_number: parsedNumber,
      country: user.country || "",
      region_id: user.region_id || "",
      age: user.age || null,
      language: Array.isArray(user.language_preference)
        ? user.language_preference
        : user.language_preference
        ? [user.language_preference]
        : [],
      sex: user.sex || "",
      documents: user.document_ids 
        ? Object.entries(user.document_ids).map(([type, id]) => ({ type, id: id as string }))
        : [],
      hrc_status: user.hrc_status ?? "NOT_INITIATED",
      mc_status: user.mc_status ?? "NOT_INITIATED",
      is_test_user: user.is_test_user ?? false,
      allow_onboarding_flow: user.allow_onboarding_flow ?? false,
    });
  }, [user, form]);

  const onSubmit = async (data: UpdateUserDetailsFormData) => {
    try {
      const fullPhoneNumber = combinePhoneNumber(
        data.phone_country_code || "+91",
        data.phone_number || ""
      );

      // Convert documents array to document_ids object
      const document_ids = data.documents?.reduce((acc, doc) => {
        if (doc.id && doc.type) {
          acc[doc.type] = doc.id;
        }
        return acc;
      }, {} as Record<string, string>) || {};

      await updateUserDetails(
        {
          user_id: user.id,
          name: data.name,
          email: data.email || null,
          phone_number: fullPhoneNumber || null,
          country: data.country || null,
          region_id: data.region_id || null,
          age: data.age || null,
          language:
            data.language && data.language.length > 0 ? data.language : null,
          sex: data.sex || null,
          document_ids: Object.keys(document_ids).length > 0 ? document_ids : null,
          hrc_status: data.hrc_status,
          mc_status: data.mc_status,
          is_test_user: data.is_test_user,
          allow_onboarding_flow: data.allow_onboarding_flow,
        },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    } catch (error) {
      console.error("Error updating user details:", error);
    }
  };

  const {
    formState: { errors, isSubmitting },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "documents",
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, (errors) => {
          toast({
            title: "Validation Error",
            description: "Please check the form for errors",
            variant: "destructive",
          });
        })}
        className="bg-white h-fit pb-8 rounded-lg overflow-x-hidden min-w-0"
      >
        <div className="bg-sidebar rounded h-16 flex items-center px-8">
          <h4 className="text-xl text-white">Edit User Profile</h4>
        </div>

        <div className="py-5 px-6 grid grid-cols-1 gap-y-7">
          <div>
            <h4>Basic Information</h4>
            <div className="mt-3 space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Enter full name"
                        className="shadow-none drop-shadow-none h-12 appearance-none"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter email"
                        className="shadow-none drop-shadow-none h-12 appearance-none"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="col-span-2">
                <FormLabel>Phone Number</FormLabel>
                <div className="flex gap-x-2 mt-2">
                  <FormField
                    control={form.control}
                    name="phone_country_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value === "NONE" ? "" : value);
                            }}
                            value={field.value || "NONE"}
                          >
                            <SelectTrigger className="h-12 shadow-none drop-shadow-none w-[180px]">
                              <SelectValue placeholder="Code" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              <SelectItem value="NONE">None</SelectItem>
                              {COUNTRY_CODES.map((country) => (
                                <SelectItem
                                  key={country.code}
                                  value={country.code}
                                >
                                  {country.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input
                            type="tel"
                            placeholder="1234567890"
                            className="shadow-none drop-shadow-none h-12 appearance-none"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-x-4">
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value === "NONE" ? "" : value);
                        }}
                        value={field.value || "NONE"}
                      >
                        <FormControl>
                          <SelectTrigger className="h-12 shadow-none drop-shadow-none">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="NONE">None</SelectItem>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="region_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Region</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value === "NONE" ? "" : value);
                        }}
                        value={field.value || "NONE"}
                      >
                        <FormControl>
                          <SelectTrigger className="h-12 shadow-none drop-shadow-none">
                            <SelectValue placeholder="Select region" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="NONE">None</SelectItem>
                          {(regions ?? []).map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {codeToFlag(r.code)} {r.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Age</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter age"
                          min={1}
                          className="shadow-none drop-shadow-none h-12"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : null
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sex"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sex</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value === "NONE" ? "" : value);
                        }}
                        value={field.value || "NONE"}
                      >
                        <FormControl>
                          <SelectTrigger className="h-12 shadow-none drop-shadow-none">
                            <SelectValue placeholder="Select sex" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="NONE">None</SelectItem>
                          {SEX_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-x-4">
                <FormField
                  control={form.control}
                  name="hrc_status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>HRC Status</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="h-12 shadow-none">
                            <SelectValue placeholder="Select HRC status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="NOT_INITIATED">
                            Not Initiated
                          </SelectItem>
                          <SelectItem value="PENDING">Pending</SelectItem>
                          <SelectItem value="APPROVED">Approved</SelectItem>
                          <SelectItem value="REJECTED">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mc_status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>MC Status</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="h-12 shadow-none">
                            <SelectValue placeholder="Select MC status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="NOT_INITIATED">
                            Not Initiated
                          </SelectItem>
                          <SelectItem value="PENDING">Pending</SelectItem>
                          <SelectItem value="APPROVED">Approved</SelectItem>
                          <SelectItem value="REJECTED">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="mt-4">
              <FormLabel>Documents</FormLabel>
              <div className="space-y-3 mt-2">
                {fields.map((field, index) => {
                  const availableTypes = DOCUMENT_TYPES.filter(
                    (dt) => 
                      form.watch(`documents.${index}.type`) === dt.value || 
                      !form.watch("documents")?.some(d => d.type === dt.value)
                  );

                  return (
                    <div key={field.id} className="flex gap-2 items-start">
                      <FormField
                        control={form.control}
                        name={`documents.${index}.type`}
                        render={({ field: formField }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Select
                                onValueChange={formField.onChange}
                                value={formField.value}
                              >
                                <SelectTrigger className="h-12 shadow-none drop-shadow-none">
                                  <SelectValue placeholder="Select document type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`documents.${index}.id`}
                        render={({ field: formField }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input
                                placeholder="Google Doc ID"
                                className="shadow-none drop-shadow-none h-12"
                                {...formField}
                                value={formField.value || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-12 w-12 mt-0"
                        onClick={() => remove(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
                
                {fields.length < DOCUMENT_TYPES.length && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => {
                      const currentDocs = form.getValues("documents") || [];
                      const availableType = DOCUMENT_TYPES.find(
                        (dt) => !currentDocs.some(d => d.type === dt.value)
                      );
                      if (availableType) {
                        append({ type: availableType.value, id: "" });
                      }
                    }}
                  >
                    + Add another doc
                  </Button>
                )}
              </div>

              <FormField
                control={form.control}
                name="language"
                render={({ field }) => {
                  const selectedLanguages = field.value || [];

                  return (
                    <FormItem>
                      <FormLabel>Language Preference</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          if (value && !selectedLanguages.includes(value)) {
                            field.onChange([...selectedLanguages, value]);
                          }
                        }}
                        value=""
                      >
                        <FormControl>
                          <SelectTrigger className="h-12 shadow-none drop-shadow-none">
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-[300px]">
                          {LANGUAGE_OPTIONS.filter(
                            (lang) => !selectedLanguages.includes(lang)
                          ).map((lang) => (
                            <SelectItem key={lang} value={lang}>
                              {lang}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedLanguages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedLanguages.map(
                            (lang: string, index: number) => (
                              <span
                                key={index}
                                className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm flex items-center gap-x-1"
                              >
                                {lang}
                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange(
                                      selectedLanguages.filter(
                                        (l: string) => l !== lang
                                      )
                                    );
                                  }}
                                  className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                                >
                                  <X size={13} />
                                </button>
                              </span>
                            )
                          )}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="is_test_user"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mt-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Test User</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Mark this account as an internal test user.
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* RH-819: care-team bypass of the "already onboarded" gate. */}
              <FormField
                control={form.control}
                name="allow_onboarding_flow"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mt-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Allow onboarding flow
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Let this returning user go through the new-user intake
                        (form, matching, first-session price) again. Turns
                        itself off once they buy the onboarding bundle.
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex items-end justify-end gap-x-4 px-6 mt-20">
          <Button
            type="submit"
            className="bg-sidebar text-white w-32 h-10 hover:bg-sidebar"
            disabled={isPending || isSubmitting}
          >
            {isPending ? "Saving..." : "Update"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
